// aeronet-brain dashboard component library. Thin React wrappers over the
// token-driven classes in components.css. Views compose these — they never write
// raw colors. Sentence case + two weights + Tabler icons are enforced by usage.
import { useEffect, useMemo, useState } from "react";
import type { ReactNode, CSSProperties, ButtonHTMLAttributes } from "react";
import { typeColor, typeTint } from "../lib/type-colors";

/** Tabler outline icon. Decorative by default (aria-hidden). */
export function Icon({ name, size, color, className, style }: { name: string; size?: number; color?: string; className?: string; style?: CSSProperties }) {
  return (
    <i
      className={`ti ti-${name}${className ? ` ${className}` : ""}`}
      style={{ fontSize: size, color, verticalAlign: size && size <= 14 ? "-2px" : undefined, ...style }}
      aria-hidden="true"
    />
  );
}

export function Card({ children, style, className }: { children: ReactNode; style?: CSSProperties; className?: string }) {
  return <div className={`kt-card${className ? ` ${className}` : ""}`} style={style}>{children}</div>;
}

export function MetricCard({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="ab-metric">
      <div className="ab-metric-label">{label}</div>
      <div className="ab-metric-value">{value}</div>
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger";
  size?: "sm" | "md";
  block?: boolean;
  icon?: string;
};
export function Button({ variant = "default", size = "md", block, icon, children, className, ...rest }: ButtonProps) {
  const cls = [
    "ab-btn",
    variant === "primary" && "ab-btn--primary",
    variant === "danger" && "ab-btn--danger",
    size === "sm" && "ab-btn--sm",
    block && "ab-btn--block",
    className,
  ].filter(Boolean).join(" ");
  return (
    <button className={cls} {...rest}>
      {icon && <Icon name={icon} size={15} />}
      {children}
    </button>
  );
}

type Tone = "info" | "success" | "warning" | "danger" | "neutral";
export function Badge({ tone = "info", icon, children }: { tone?: Tone; icon?: string; children: ReactNode }) {
  const cls = tone === "info" ? "ab-badge" : `ab-badge ab-badge--${tone}`;
  return <span className={cls}>{icon && <Icon name={icon} size={12} />}{children}</span>;
}

export function Pill({ icon, children, onClick, href, title }: { icon?: string; children: ReactNode; onClick?: () => void; href?: string; title?: string }) {
  const inner = <>{icon && <Icon name={icon} size={13} />}{children}</>;
  if (href) return <a className="ab-pill" href={href} title={title}>{inner}</a>;
  if (onClick) return <button className="ab-pill" onClick={onClick} title={title}>{inner}</button>;
  return <span className="ab-pill" title={title}>{inner}</span>;
}

/** scope → tier-coloured pill (personal / dept:* / company). */
export function ScopePill({ scope }: { scope: string }) {
  const s = scope === "private" ? "personal" : scope === "shared" ? "company" : scope;
  if (s === "company") return <span className="ab-pill ab-scope--company"><Icon name="building" size={13} />company</span>;
  if (s.startsWith("dept:")) return <span className="ab-pill ab-scope--dept"><Icon name="stack-2" size={13} />{s.slice(5)}</span>;
  if (s.startsWith("personal")) return <span className="ab-pill ab-scope--personal"><Icon name="user" size={13} />personal</span>;
  return <span className="ab-pill">{s}</span>;
}

/** who/what proposed a fact + optional source link. */
export function ProvenanceChip({ by, synthesized, sourceUrl }: { by: string; synthesized?: boolean; sourceUrl?: string }) {
  return (
    <span className="ab-provenance">
      <Icon name={synthesized ? "robot" : "user-check"} size={13} />
      {synthesized ? `synthesized by ${by}` : `from ${by}`}
      {sourceUrl && <a className="ab-mono" href={sourceUrl} target="_blank" rel="noreferrer" style={{ color: "var(--color-text-info)" }}>source</a>}
    </span>
  );
}

/** "verified 3d ago" / "last seen Mar — may be stale". */
export function FreshnessBadge({ verifiedDaysAgo, stale }: { verifiedDaysAgo?: number | null; stale?: boolean }) {
  const text =
    verifiedDaysAgo == null ? "verification unknown" :
    stale ? `verified ${verifiedDaysAgo}d ago — may be stale` :
    `verified ${verifiedDaysAgo}d ago`;
  return <span className={`ab-freshness${stale ? " ab-freshness--stale" : ""}`}>{text}</span>;
}

export function BriefRow({ icon, iconTone = "secondary", children, action }: {
  icon: string; iconTone?: "info" | "secondary" | "warning" | "success" | "danger"; children: ReactNode; action?: ReactNode;
}) {
  const color = iconTone === "secondary" ? "var(--color-text-secondary)" : `var(--color-text-${iconTone})`;
  return (
    <div className="ab-brief-row">
      <Icon name={icon} size={17} color={color} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {action}
    </div>
  );
}

export function EmptyState({ icon = "circle-check", title, children }: { icon?: string; title: string; children?: ReactNode }) {
  return (
    <div className="ab-empty">
      <Icon name={icon} />
      <div className="ab-h3" style={{ color: "var(--color-text-primary)" }}>{title}</div>
      {children && <div className="ab-small ab-hint" style={{ marginTop: 4 }}>{children}</div>}
    </div>
  );
}

/** Page heading block: title + optional date/eyebrow + actions. */
export function PageHeader({ title, eyebrow, actions }: { title: string; eyebrow?: ReactNode; actions?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div>
        <div className="ab-h1">{title}</div>
        {eyebrow && <div className="ab-eyebrow">{eyebrow}</div>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return <div className="ab-muted ab-small" style={{ padding: 24, textAlign: "center" }}>{label ?? "Loading…"}</div>;
}

/* ============================================================================
 * KNOWLEDGE-TIER REVAMP — shared primitives.
 * Thin wrappers over the .kt-* classes in components.css. Pages compose these;
 * they never write raw colors. Tabler icons (ti ti-*) throughout.
 * ========================================================================== */

export type EntityStatus = "active" | "onboarding" | "paused";
export type SpineTone = "red" | "green" | "teal";

/** Resolve a completeness % + status to the row spine tone (handoff rules). */
export function spineTone(pct: number, status?: EntityStatus): SpineTone {
  if (status === "paused" || pct <= 0) return "red";
  if (pct >= 100) return "green";
  return "teal";
}

/** Deterministic monogram tint palette — shared by list tables + detail headers. */
const MONOGRAM_TINTS: [string, string][] = [
  ["#dcedea", "#0c6f68"],
  ["#e5d8d0", "#7a4a35"],
  ["#d8dde8", "#4a5b7a"],
  ["#ece3c0", "#7a5d18"],
  ["#e6d5e0", "#7a4068"],
  ["#d5e5dc", "#326652"],
];
/** Hash a seed (id) to one [bg, fg] tint pair — stable across renders. */
export function monogramTint(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return MONOGRAM_TINTS[h % MONOGRAM_TINTS.length];
}
/** First+second word initials, uppercased. */
export function monogramInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase()) || "?";
}
/** Tinted monogram tile: deterministic initials on a hashed tint. `shape`: rounded (default,
 *  customers) · box (projects) · circle (people). Tint seeded by `seed` (falls back to `name`). */
export function Monogram({ name, seed, size = 36, shape = "rounded" }: {
  name: string; seed?: string; size?: number; shape?: "rounded" | "box" | "circle";
}) {
  const [bg, fg] = monogramTint(seed ?? name);
  const radius = shape === "circle" ? "50%" : Math.round(size * (shape === "box" ? 0.28 : 0.22));
  return (
    <span className="kt-monogram" style={{ width: size, height: size, borderRadius: radius, background: bg, color: fg, fontSize: Math.round(size * 0.34) }}>
      {monogramInitials(name)}
    </span>
  );
}

/** `Customers › Acme › Migration` — last item rendered as current (no link). */
export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="kt-breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {item.to && !last ? (
              <a href={item.to}>{item.label}</a>
            ) : (
              <span className={last ? "kt-crumb-current" : undefined}>{item.label}</span>
            )}
            {!last && <span className="kt-crumb-sep" aria-hidden="true">›</span>}
          </span>
        );
      })}
    </nav>
  );
}

/** Header-band status badge: colored dot + label on a soft tint. */
export function StatusBadge({ status }: { status: EntityStatus }) {
  return (
    <span className={`kt-status-badge kt-status--${status}`}>
      <span className="kt-status-dot" />{status}
    </span>
  );
}

/** Bare colored status dot (for list group headers / recent lists). */
export function StatusDot({ status, color }: { status?: EntityStatus; color?: string }) {
  const cls = status ? `kt-status-dot kt-status--${status}` : "kt-status-dot";
  return <span className={cls} style={color ? { background: color } : undefined} />;
}

/** 6px progress track + teal/red/green fill. */
export function ProgressBar({ value, tone = "teal" }: { value: number; tone?: SpineTone }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="kt-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className={`kt-progress-fill kt-progress-fill--${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Bordered radius-8 list panel wrapper. */
export function ListPanel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`kt-list-panel${className ? ` ${className}` : ""}`}>{children}</div>;
}

/** List group header: bold title + right cluster + optional collapse chevron. */
export function GroupHeader({ title, rightCluster, collapsed, onToggle }: {
  title: ReactNode; rightCluster?: ReactNode; collapsed?: boolean; onToggle?: () => void;
}) {
  return (
    <div className="kt-group-header">
      <div className="kt-group-header-title">{title}</div>
      <div className="kt-group-header-right">
        {rightCluster}
        {onToggle && (
          <button className="kt-collapse-btn" onClick={onToggle} aria-label={collapsed ? "Expand" : "Collapse"} aria-expanded={!collapsed}>
            <Icon name={collapsed ? "chevron-down" : "chevron-up"} />
          </button>
        )}
      </div>
    </div>
  );
}

export interface EntityRowProps {
  name: ReactNode;
  tier?: string;
  product?: string;
  entityKey?: string;          // mono key e.g. customer:flightcare
  percent: number;
  doneLabel?: ReactNode;       // e.g. "18/24 atoms"
  status?: EntityStatus;
  metaPills?: ReactNode[];     // e.g. [csm, domain]
  date?: ReactNode;
  to?: string;                 // navigate target (anchor href)
  onClick?: () => void;
}
/** Clickable dense list row: status spine + title/progress/percent/meta grid. */
export function EntityRow({ name, tier, product, entityKey, percent, doneLabel, status, metaPills, date, to, onClick }: EntityRowProps) {
  const tone = spineTone(percent, status);
  const grid = (
    <>
      <span className={`kt-row-spine kt-spine--${tone}`} />
      <div className="kt-entity-grid">
        <div className="kt-row-titleblock">
          <div className="kt-row-titleline">
            <span className="kt-row-name">{name}</span>
            {tier && <span className="kt-tier-pill">{tier}</span>}
            {product && <span className="kt-product-pill">{product}</span>}
          </div>
          {entityKey && <div className="kt-row-key">{entityKey}</div>}
        </div>
        <div>
          <ProgressBar value={percent} tone={tone} />
          {doneLabel && <div className="kt-row-donelabel">{doneLabel}</div>}
        </div>
        <div className={`kt-row-pct${percent <= 0 ? " kt-row-pct--zero" : ""}`}>{percent}%</div>
        <div className="kt-row-meta">
          {metaPills && metaPills.length > 0 && (
            <div className="kt-row-metapills">
              {metaPills.map((p, i) => <span key={i} className="kt-meta-pill">{p}</span>)}
            </div>
          )}
          {date && <div className="kt-row-date">{date}</div>}
        </div>
      </div>
    </>
  );
  if (to) return <a className="kt-entity-row" href={to}>{grid}</a>;
  return <div className="kt-entity-row" onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>{grid}</div>;
}

export interface TabSpec { key: string; label: ReactNode; }
/** Border-bottom tab rail; active = teal text + 2px teal underline. */
export function TabRail({ tabs, activeTab, onChange }: { tabs: TabSpec[]; activeTab: string; onChange: (key: string) => void }) {
  return (
    <div className="kt-tab-rail" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={t.key === activeTab}
          className={`kt-tab${t.key === activeTab ? " active" : ""}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Strip-bg header band: teal title + status badge + mono key; right actions. */
export function EntityHeader({ name, status, monoKey, actions }: {
  name: ReactNode; status?: EntityStatus; monoKey?: string; actions?: ReactNode;
}) {
  return (
    <div className="kt-entity-header">
      <div>
        <div className="kt-entity-header-title">
          <h1 className="kt-entity-name">{name}</h1>
          {status && <StatusBadge status={status} />}
        </div>
        {monoKey && <div className="kt-entity-key">{monoKey}</div>}
      </div>
      {actions && <div className="kt-entity-actions">{actions}</div>}
    </div>
  );
}

export interface RecordField { label: ReactNode; value: ReactNode; mono?: boolean; strong?: boolean; }
/** Mono RECORD label + key/value rows; align-self:start. */
export function RecordCard({ label = "Record", fields }: { label?: string; fields: RecordField[] }) {
  return (
    <div className="kt-record-card">
      <div className="kt-record-label">{label}</div>
      <div className="kt-record-rows">
        {fields.map((f, i) => (
          <div key={i} className="kt-record-row">
            <span className="kt-record-key">{f.label}</span>
            <span className={`kt-record-val${f.mono ? " kt-record-val--mono" : ""}${f.strong ? " kt-record-val--strong" : ""}`}>{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface AtomFacet { label: string; to?: string; onClick?: () => void; }
export interface AtomRowProps {
  type: string;                // data-model type (drives spine/swatch/label)
  typeLabel?: ReactNode;       // defaults to `type`
  source?: ReactNode;          // source pill, e.g. "Jira"
  scope?: AtomFacet | ReactNode; // clickable facet chip or plain node
  age?: ReactNode;             // right-aligned, e.g. "2h"
  title: ReactNode;
  body?: ReactNode;
  children?: ReactNode;        // expand-in-place content (Phase B)
}
/** Activity row: type-color spine + meta line + title + body. */
export function AtomRow({ type, typeLabel, source, scope, age, title, body, children }: AtomRowProps) {
  const color = typeColor(type);
  const tint = typeTint(type);
  let scopeNode: ReactNode = null;
  if (scope && typeof scope === "object" && scope !== null && "label" in (scope as object)) {
    const f = scope as AtomFacet;
    scopeNode = f.to
      ? <a className="kt-atom-scope" href={f.to}>{f.label}</a>
      : f.onClick
        ? <button className="kt-atom-scope" onClick={f.onClick}>{f.label}</button>
        : <span className="kt-atom-scope">{f.label}</span>;
  } else if (scope) {
    scopeNode = <span className="kt-atom-scope">{scope as ReactNode}</span>;
  }
  return (
    <div className="kt-atom-row">
      <span className="kt-atom-spine" style={{ background: color }} />
      <div className="kt-atom-body">
        <div className="kt-atom-meta">
          <span className="kt-atom-swatch" style={{ background: tint, border: `1px solid ${color}` }} />
          <span className="kt-atom-typelabel">{typeLabel ?? type}</span>
          {source && <span className="kt-atom-source">{source}</span>}
          {scopeNode}
          <span className="kt-atom-spacer" />
          {age && <span className="kt-atom-age">{age}</span>}
        </div>
        <div className="kt-atom-title">{title}</div>
        {body && <div className="kt-atom-text">{body}</div>}
        {children}
      </div>
    </div>
  );
}

/** Mono uppercase date-group label, e.g. "TODAY" / "THIS WEEK". */
export function DateGroupLabel({ children }: { children: ReactNode }) {
  return <div className="kt-date-group">{children}</div>;
}

/** Rows-per-page + range + prev/next chevrons. */
export function Pagination({ page, pageCount, rangeLabel, rowsPerPage, onPrev, onNext }: {
  page: number; pageCount: number; rangeLabel: ReactNode; rowsPerPage?: number;
  onPrev?: () => void; onNext?: () => void;
}) {
  return (
    <div className="kt-pagination">
      {rowsPerPage != null && (
        <div className="kt-pagination-rows">Rows per page: <b>{rowsPerPage}</b></div>
      )}
      <div>{rangeLabel}</div>
      <div className="kt-pagination-chevrons">
        <button onClick={onPrev} disabled={page <= 1} aria-label="Previous page"><Icon name="chevron-left" /></button>
        <button onClick={onNext} disabled={page >= pageCount} aria-label="Next page"><Icon name="chevron-right" /></button>
      </div>
    </div>
  );
}

export interface FilterRow {
  key: string;
  label: ReactNode;
  count?: ReactNode;
  dotColor?: string;     // status-dot color (status filters)
  swatchColor?: string;  // square swatch color (type legend)
  checked?: boolean;     // checkbox state (multi-select tier/type)
}
/** Sidebar filter block: mono label + selectable rows. Single- or multi-select. */
export function FilterGroup({ label, rows, mode = "single", selected, onSelect }: {
  label: string;
  rows: FilterRow[];
  mode?: "single" | "multi";
  selected: string | string[];
  onSelect: (key: string) => void;
}) {
  const isActive = (key: string) =>
    Array.isArray(selected) ? selected.includes(key) : selected === key;
  return (
    <div className="kt-filter-group">
      <div className="kt-filter-label">{label}</div>
      {rows.map((r) => {
        const active = isActive(r.key);
        return (
          <button key={r.key} className={`kt-filter-row${active && mode === "single" ? " active" : ""}`} onClick={() => onSelect(r.key)}>
            <span className="kt-filter-row-label">
              {mode === "multi" && r.dotColor == null && r.swatchColor == null && (
                <Icon name={r.checked ? "square-check" : "square"} className={`kt-filter-checkbox${r.checked ? " checked" : ""}`} />
              )}
              {r.dotColor && <span className="kt-status-dot" style={{ background: r.dotColor }} />}
              {r.swatchColor && <span className="kt-filter-swatch" style={{ background: r.swatchColor }} />}
              {r.label}
            </span>
            {r.count != null && <span className="kt-filter-row-count">{r.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ============================================================================
 * Foundation primitives (forms, table, modal, alert, grid, loaders) — kt-*.
 * Added in the MUI→kt consolidation; pages compose these instead of MUI.
 * ========================================================================== */

/** Centered spinner + optional label — full-panel loading / auth-checking. */
export function CenteredSpinner({ label }: { label?: string }) {
  return <div className="kt-center"><span className="kt-spinner" />{label && <span style={{ fontSize: 13 }}>{label}</span>}</div>;
}

/** Shimmer placeholder. */
export function Skeleton({ height = 60, width, style }: { height?: number | string; width?: number | string; style?: CSSProperties }) {
  return <div className="kt-skel" style={{ height, width, ...style }} />;
}

/** Responsive grid. cols="2"|"3" forces a column count; otherwise min-width auto-fill. */
export function Grid({ cols, min, gap, children, style }: { cols?: "2" | "3"; min?: number; gap?: number; children: ReactNode; style?: CSSProperties }) {
  return <div className={`kt-grid${cols ? ` kt-grid--${cols}` : ""}`} style={{ ...(min ? { ["--kt-grid-min" as string]: `${min}px` } : {}), ...(gap != null ? { gap } : {}), ...style }}>{children}</div>;
}

type AlertTone = "info" | "success" | "warning" | "danger";
const ALERT_ICON: Record<AlertTone, string> = { info: "info-circle", success: "circle-check", warning: "alert-triangle", danger: "alert-circle" };
export function Alert({ tone = "info", icon, children, style }: { tone?: AlertTone; icon?: string; children: ReactNode; style?: CSSProperties }) {
  return <div className={`kt-alert kt-alert--${tone}`} style={style}><Icon name={icon ?? ALERT_ICON[tone]} size={17} /><div style={{ flex: 1, minWidth: 0 }}>{children}</div></div>;
}

/** Text input with optional label/hint. Forwards all native input props. */
export function TextField({ label, hint, className, ...rest }: { label?: ReactNode; hint?: ReactNode } & React.InputHTMLAttributes<HTMLInputElement>) {
  const input = <input className={`kt-input${className ? ` ${className}` : ""}`} {...rest} />;
  if (!label && !hint) return input;
  return <label className="kt-field">{label && <span className="kt-field-label">{label}</span>}{input}{hint && <span className="kt-field-hint">{hint}</span>}</label>;
}

export function TextArea({ label, hint, className, ...rest }: { label?: ReactNode; hint?: ReactNode } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const el = <textarea className={`kt-textarea${className ? ` ${className}` : ""}`} {...rest} />;
  if (!label && !hint) return el;
  return <label className="kt-field">{label && <span className="kt-field-label">{label}</span>}{el}{hint && <span className="kt-field-hint">{hint}</span>}</label>;
}

export function SelectField({ label, hint, className, children, ...rest }: { label?: ReactNode; hint?: ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const el = <select className={`kt-select${className ? ` ${className}` : ""}`} {...rest}>{children}</select>;
  if (!label && !hint) return el;
  return <label className="kt-field">{label && <span className="kt-field-label">{label}</span>}{el}{hint && <span className="kt-field-hint">{hint}</span>}</label>;
}

export function Checkbox({ label, ...rest }: { label?: ReactNode } & React.InputHTMLAttributes<HTMLInputElement>) {
  return <label className="kt-checkrow"><input type="checkbox" {...rest} />{label}</label>;
}

/** Sortable, optionally clickable table. Replaces the MUI DataGrid for admin lists. */
export type Column<R> = { key: string; header: ReactNode; render?: (row: R) => ReactNode; sortValue?: (row: R) => string | number; width?: number | string; align?: "left" | "right" | "center" };
export function DataTable<R>({ columns, rows, getRowId, onRowClick, empty = "No rows.", initialSort }:
  { columns: Column<R>[]; rows: R[]; getRowId: (row: R, i: number) => string | number; onRowClick?: (row: R) => void; empty?: ReactNode; initialSort?: { key: string; dir: "asc" | "desc" } }) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(initialSort ?? null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const f = col.sortValue;
    return [...rows].sort((a, b) => {
      const va = f(a), vb = f(b);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);
  const toggle = (key: string) => setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return (
    <div className="kt-tablewrap">
      <table className="kt-table">
        <thead><tr>{columns.map((c) => {
          const sortable = !!c.sortValue;
          const arrow = sort?.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : "";
          return <th key={c.key} className={sortable ? "kt-th--sortable" : undefined} style={{ width: c.width, textAlign: c.align }} onClick={sortable ? () => toggle(c.key) : undefined}>{c.header}{arrow}</th>;
        })}</tr></thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td className="kt-table-empty" colSpan={columns.length}>{empty}</td></tr>
          ) : sorted.map((row, i) => (
            <tr key={getRowId(row, i)} className={onRowClick ? "kt-tr--clickable" : undefined} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }}>{c.render ? c.render(row) : (row as Record<string, ReactNode>)[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Centered modal dialog. Closes on backdrop click / Escape. */
export function Modal({ title, open, onClose, children, actions, width }: { title?: ReactNode; open: boolean; onClose: () => void; children: ReactNode; actions?: ReactNode; width?: number }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="kt-dialog-backdrop" onClick={onClose} role="presentation">
      <div className="kt-dialog" style={width ? { maxWidth: width } : undefined} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {title && <div className="kt-dialog-title">{title}</div>}
        {children}
        {actions && <div className="kt-dialog-actions">{actions}</div>}
      </div>
    </div>
  );
}
